import { useState } from "react";
import { Box, Typography, useTheme, IconButton, Stack, Button } from "@mui/material";
import { tokens } from "../../../assets/theme";
import { FormattedMessage, useIntl } from "react-intl";
import messages from "./messages";
import { useQuery } from "@tanstack/react-query";
import { fetchOeeGridDateIntervall, fetchOeeDashboardDataTimeSpan } from "../../../fetchers/oee";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import IconInformation from '@mui/icons-material/InfoOutlined';
import HdrAutoOutlined from "@mui/icons-material/HdrAutoOutlined";
import StartIcon from '@mui/icons-material/Start';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import InputOutlinedIcon from '@mui/icons-material/InputOutlined';
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import DateFilter from "./DateFilter";
import dayjs from "dayjs";
import { HideIfNoRole } from "../../../auth/authUi";
import { isFeatureEnabled } from "../../../hooks/useFeatureFlags";
import { formatMinutesAsDuration } from "../../../utils/durationUtils";
import { isoFormat, formatDateFullTime, formatDateTime, formatDate, formatTime, formatDateTimeISO } from "../../../utils/dateTimeUtils";
import { Fragment } from "react";
import ActivePOUpdateDialog from "../../activeProductionOrders/components/activePOUpdateDialog";
import Popup from '../../../components/Popup';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import MachineReportIssueButton from "./MachineReportIssueButton";
import FittedMachineText from "./FittedMachineText";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import PauseCircleOutlineOutlinedIcon from '@mui/icons-material/PauseCircleOutlineOutlined';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';


const Infoboxes = (props) => {
    const language = localStorage.getItem('language') || 'en'
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { formatMessage } = useIntl();
    const [openPopup, setOpenPopup] = useState(false);
    const [activePOAction, setActivePOAction] = useState(null);

    const machineDetailsQuery = useQuery(
        ["oeedata", props.timeSpan, props.companyKey],
        () =>
            fetchOeeDashboardDataTimeSpan(props.timeSpan, props.companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000,
            enabled: !props.useDateInterval,
            keepPreviousData: true
        }
    );

    const machineDetailsDateIntervalQuery = useQuery(
        [
            "oeedataDatePicker", props.startDate, props.endDate, props.companyKey
        ],
        () =>
            fetchOeeGridDateIntervall(props.startDate, props.endDate, props.companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000,
            enabled: props.useDateInterval,
            keepPreviousData: true
        }
    );

    if (props.machineInfo == null) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="73vh"
            >
                <Typography variant="h5" color="error">
                    machineInfo or machineDetails are missing for current time range
                </Typography>
            </Box>
        );
    }

    //MachineInfo
    let machineStatus = "";
    let currentStop = "";
    let percentageDone = "";
    let percentageDoneMax = 0;
    let runningMachine = "";
    let OEEPercentBad = "";
    let OEEPercentWarning = "";
    let grafanaLink = "";
    let machineInfo = {};

    if (
        props.machineInfo !== undefined &&
        props.machineInfo !== null &&
        !isNaN(props.machineInfo.percentageDone)
    ) {
        machineInfo = props.machineInfo;
        currentStop = `(${machineInfo.currentStop})`;
        machineStatus = props.machineStatusForMachine ? props.machineStatusForMachine.machineStatus : machineInfo.machineStatus;

        runningMachine = props.machineStatusForMachine ? props.machineStatusForMachine.machineStatus === "Running" : machineInfo.machineStatus === "Running";
        OEEPercentBad = machineInfo.oeePercentBad;
        OEEPercentWarning = machineInfo.oeePercentWarning;
        grafanaLink = props.company?.grafanaBaseUrl?.replace(
            "{machineName}",
            props.machineName
        );
    }

    //MachineDetails
    let t = "";
    let a = "";
    let k = "";
    let takt = "";
    let tak = "";
    let idealTakt = "";
    let numberOfCyclesWithCavity = "";
    let lastCycleTime = "";
    let idealCycleTime = "";
    let scrappedQty = "";
    let remainingTimeRaw = "";
    let uptime = "";
    let downTime = "";
    let downTimeNoOee = "";
    let machineDetails = {};
    
    // ---- Is this better located than the one below? --- 
    percentageDone = Math.round(
        (machineInfo.reportedQty / machineInfo.plannedQty) * 100
    );
    if (isNaN(percentageDone)) {
            percentageDone = 0;
            percentageDoneMax = 0;
    }
    // ----------

    if (
        (props.useDateInterval === false &&
            machineDetailsQuery.data !== undefined &&
            machineDetailsQuery.data?.filter(m => String(m.id) === String(props.machineId)).length > 0) ||
        (props.useDateInterval === true &&
            machineDetailsDateIntervalQuery.data !== undefined &&
            machineDetailsDateIntervalQuery?.data.filter(m => String(m.id) === String(props.machineId)).length > 0)
    ) {
        machineDetails =
            props.useDateInterval === false
                ? machineDetailsQuery.data?.find(m => String(m.id) === String(props.machineId))
                : machineDetailsDateIntervalQuery.data?.find(m => String(m.id) === String(props.machineId));
        // ---- Is this better located than the one above? ---
        percentageDone = Math.round(
            (machineDetails.reportedQty / machineDetails.plannedQty) * 100
        );
        percentageDoneMax = Math.round(
            (machineDetails.reportedQty / machineDetails.plannedQty) * 100
        );
        if (isNaN(percentageDone)) {
            percentageDone = 0;
            percentageDoneMax = 0;
        }
        if (isNaN(percentageDone)) {
            percentageDoneMax = 0;
        }

        lastCycleTime = machineDetails.lastCycleTime;
        idealCycleTime = machineDetails.idealCycleTimeActive;
        // Calculate remaining time
        remainingTimeRaw = machineDetails.remainingTime;
        // Calculate OEE
        t = machineDetails.t;
        a = machineDetails.a;
        k = machineDetails.k;
        takt = machineDetails.lastPace;
        idealTakt = machineDetails.idealPace;
        if (props.machine?.meassurePerformance === false) {
            a = 100;
        }
        if (props.machine?.meassureQuality === false) {
            k = 100;
        }
        let aCalc = a;
        if (aCalc > 100) {
            aCalc = 100;
        }
        tak = (t / 100) * (aCalc / 100) * (k / 100) * 100;
        numberOfCyclesWithCavity = machineDetails.numberOfCyclesWithCavity;
        scrappedQty = machineDetails.scrappedQty;
        uptime = machineDetails.uptime;
        downTime = machineDetails.downTime;
        downTimeNoOee = machineDetails.downTimeNoOee;
    }

    function roundOrEmptystring(value, attributeName) {
        if (value === undefined || value === null || value === "") {
            return "";
        }
        if (
            attributeName === "t" &&
            props.machine?.meassureAvailability === false
        ) {
            return " - ";
        } else if (
            attributeName === "a" &&
            props.machine?.meassurePerformance === false
        ) {
            return " - ";
        } else if (
            attributeName === "k" &&
            props.machine?.meassureQuality === false
        ) {
            return " - ";
        }
        return Math.round(value);
    }
    function showStringIfNotEmptyValue(value, string, attributeName) {
        if (value === undefined || value === null || value === "") {
            return "";
        } else if (
            attributeName === "t" &&
            props.machine?.meassureAvailability === false
        ) {
            return "";
        } else if (
            attributeName === "a" &&
            props.machine?.meassurePerformance === false
        ) {
            return "";
        } else if (
            attributeName === "k" &&
            props.machine?.meassureQuality === false
        ) {
            return "";
        }
        return string;
    }

    function cycleTimeColor(lastCT, idealCT) {
        if (lastCT === 0 || lastCT === "") {
            return colors.primary[400];
        }

        var yellowExpandPercent = 0.05;
        var redExpandPercent = 0.1;
        var result = colors.dashbordGreen[600];
        if (lastCT > idealCT + idealCT * redExpandPercent) {
            result = colors.redAccent[500];
        } else if (lastCT > idealCT + idealCT * yellowExpandPercent) {
            result = colors.yellow[500];
        }

        return result;
    }

    function paceColor(pace, idealPace) {
        const currentPace = Number(pace);
        const targetPace = Number(idealPace);

        if (
            !Number.isFinite(currentPace) ||
            !Number.isFinite(targetPace) ||
            currentPace <= 0 ||
            targetPace <= 0
        ) {
            return colors.primary[400];
        }

        if (currentPace < targetPace * 0.90) {
            return colors.redAccent[500];
        }

        if (currentPace < targetPace * 0.95) {
            return colors.yellow[500];
        }

        return colors.dashbordGreen[600];
    }

    function getLastCycleTimeTitle() {
        if (!isFeatureEnabled(props.featureFlags, 'ShowSpeedUnitType')) {
            return formatMessage(messages.lastCycleTime);
        }
        if (props.machine?.speedUnitType?.id === 2 || props.machine?.speedUnitType?.id === 3 || props.machine?.speedUnitType?.id === 4 || props.machine?.speedUnitType?.id === 5) {
            return formatMessage(messages.pace)
        }
        return formatMessage(messages.lastCycleTime);
    }

    function getIdealCycleTimeTitle() {
        if (!isFeatureEnabled(props.featureFlags, 'ShowSpeedUnitType')) {
            return formatMessage(messages.idealCycleTimeActive);
        }
        if (props.machine?.speedUnitType?.id === 2 || props.machine?.speedUnitType?.id === 3 || props.machine?.speedUnitType?.id === 4 || props.machine?.speedUnitType?.id === 5) {
            return `${formatMessage(messages.idealPace)}`;
        }
        return formatMessage(messages.idealCycleTimeActive);
    }

    function getAvalabilityThresholds() {
        let percentBad = 65;
        let percentWarning = 78;

        if (
            props.machine?.availabilityPercentBad !== undefined &&
            props.machine?.availabilityPercentBad !== null
        ) {
            percentBad = props.machine.availabilityPercentBad;
        } else if (
            props.company?.availabilityPercentBad !== undefined &&
            props.company?.availabilityPercentBad !== null
        ) {
            percentBad = props.company.availabilityPercentBad;
        } else if (
            props.machine?.oeePercentBad !== undefined &&
            props.machine?.oeePercentBad !== null
        ) {
            percentBad = props.machine.oeePercentBad;
        } else if (
            props.company?.oeePercentBad !== undefined &&
            props.company?.oeePercentBad !== null
        ) {
            percentBad = props.company.oeePercentBad;
        }

        if (
            props.machine?.availabilityPercentWarning !== undefined &&
            props.machine?.availabilityPercentWarning !== null
        ) {
            percentWarning = props.machine.availabilityPercentWarning;
        } else if (
            props.company?.availabilityPercentBad !== undefined &&
            props.company?.availabilityPercentBad !== null
        ) {
            percentWarning = props.company.availabilityPercentWarning;
        } else if (
            props.machine?.oeePercentBad !== undefined &&
            props.machine?.oeePercentWarning !== null
        ) {
            percentWarning = props.machine.oeePercentWarning;
        } else if (
            props.company?.oeePercentBad !== undefined &&
            props.company?.oeePercentBad !== null
        ) {
            percentWarning = props.company.oeePercentWarning;
        }

        return { percentBad: percentBad, percentWarning: percentWarning };
    }

    function getPerformanceThresholds() {
        let percentBad = 65;
        let percentWarning = 78;

        if (
            props.machine?.performancePercentBad !== undefined &&
            props.machine?.performancePercentBad !== null
        ) {
            percentBad = props.machine.performancePercentBad;
        } else if (
            props.company?.performancePercentBad !== undefined &&
            props.company?.performancePercentBad !== null
        ) {
            percentBad = props.company.performancePercentBad;
        } else if (
            props.machine?.oeePercentBad !== undefined &&
            props.machine?.oeePercentBad !== null
        ) {
            percentBad = props.machine.oeePercentBad;
        } else if (
            props.company?.oeePercentBad !== undefined &&
            props.company?.oeePercentBad !== null
        ) {
            percentBad = props.company.oeePercentBad;
        }

        if (
            props.machine?.performancePercentWarning !== undefined &&
            props.machine?.performancePercentWarning !== null
        ) {
            percentWarning = props.machine.performancePercentWarning;
        } else if (
            props.company?.performancePercentWarning !== undefined &&
            props.company?.performancePercentWarning !== null
        ) {
            percentWarning = props.company.performancePercentWarning;
        } else if (
            props.machine?.oeePercentBad !== undefined &&
            props.machine?.oeePercentWarning !== null
        ) {
            percentWarning = props.machine.oeePercentWarning;
        } else if (
            props.company?.oeePercentBad !== undefined &&
            props.company?.oeePercentBad !== null
        ) {
            percentWarning = props.company.oeePercentWarning;
        }

        return { percentBad: percentBad, percentWarning: percentWarning };
    }

    function getQualityThresholds() {
        let percentBad = 65;
        let percentWarning = 78;

        if (
            props.machine?.qualityPercentBad !== undefined &&
            props.machine?.qualityPercentBad !== null
        ) {
            percentBad = props.machine.qualityPercentBad;
        } else if (
            props.company?.qualityPercentBad !== undefined &&
            props.company?.qualityPercentBad !== null
        ) {
            percentBad = props.company.performancePercentBad;
        } else if (
            props.machine?.oeePercentBad !== undefined &&
            props.machine?.oeePercentBad !== null
        ) {
            percentBad = props.machine.oeePercentBad;
        } else if (
            props.company?.oeePercentBad !== undefined &&
            props.company?.oeePercentBad !== null
        ) {
            percentBad = props.company.oeePercentBad;
        }

        if (
            props.machine?.qualityPercentWarning !== undefined &&
            props.machine?.qualityPercentWarning !== null
        ) {
            percentWarning = props.machine.qualityPercentWarning;
        } else if (
            props.company?.qualityPercentWarning !== undefined &&
            props.company?.qualityPercentWarning !== null
        ) {
            percentWarning = props.company.qualityPercentWarning;
        } else if (
            props.machine?.oeePercentBad !== undefined &&
            props.machine?.oeePercentWarning !== null
        ) {
            percentWarning = props.machine.oeePercentWarning;
        } else if (
            props.company?.oeePercentBad !== undefined &&
            props.company?.oeePercentBad !== null
        ) {
            percentWarning = props.company.oeePercentWarning;
        }

        return { percentBad: percentBad, percentWarning: percentWarning };
    }

    function getOeeThresholds() {
        let percentBad = 65;
        let percentWarning = 78;

        if (
            props.machine?.oeePercentBad !== undefined &&
            props.machine?.oeePercentBad !== null
        ) {
            percentBad = props.machine.oeePercentBad;
        } else if (
            props.company?.oeePercentBad !== undefined &&
            props.company?.oeePercentBad !== null
        ) {
            percentBad = props.company.oeePercentBad;
        }

        if (
            props.machine?.oeePercentBad !== undefined &&
            props.machine?.oeePercentWarning !== null
        ) {
            percentWarning = props.machine.oeePercentWarning;
        } else if (
            props.company?.oeePercentBad !== undefined &&
            props.company?.oeePercentBad !== null
        ) {
            percentWarning = props.company.oeePercentWarning;
        }

        return { percentBad: percentBad, percentWarning: percentWarning };
    }

    function getBoxColor(value, attributeName) {
        if (value === "") {
            return colors.primary[400];
        }

        let percentBad = 65;
        let percentWarning = 78;
        if (attributeName === "t") {
            ({ percentBad = percentBad, percentWarning = percentWarning } =
                getAvalabilityThresholds());
        } else if (attributeName === "a") {
            ({ percentBad = percentBad, percentWarning = percentWarning } =
                getPerformanceThresholds());
        } else if (attributeName === "k") {
            ({ percentBad = percentBad, percentWarning = percentWarning } =
                getQualityThresholds());
        } else if (attributeName === "tak") {
            ({ percentBad = percentBad, percentWarning = percentWarning } =
                getOeeThresholds());
        }

        if (attributeName === "a" && props.machine?.meassurePerformance === false) {
            return colors.primary[400];
        } else if (
            attributeName === "k" &&
            props.machine?.meassureQuality === false
        ) {
            return colors.primary[400];
        }
        return value > percentWarning
            ? colors.dashbordGreen[600]
            : value > percentBad && value <= percentWarning
                ? colors.yellow[500]
                : colors.redAccent[500]
    }

    function handleGrafanaLinkClick() {
        if (props.machine?.useStopPageOnGrafanaLink)
            window.open(`/machineDashboard/${props.machine?.id}`, "_blank");
        else {
            window.open(grafanaLink, "_blank");
        }
    }

    function getItemInProduction(item, itemName) {
        if (item === "#NA" || item === "" || item === null || item === undefined) {
            return itemName;
        }
        return item + " - " + itemName;
    }

    function formatFullDateTime(date) {
        return date ? formatDateFullTime(language, date) : "";
    }

    function getSpeedTypeValueDividedByValueRounded(speedtypeValue, value) {
        const numerator = Number(speedtypeValue);
        const denominator = Number(value);

        if (
            !Number.isFinite(numerator) ||
            !Number.isFinite(denominator) ||
            numerator <= 0 ||
            denominator <= 0
        ) {
            return "-";
        }

        const result = numerator / denominator;

        return Number.isFinite(result) ? Math.round(result) : "-";
    }

    function formatToFixed(value, decimals) {
        //Round to specified number of decimal places
        if (value === undefined || value === null || isNaN(value)) {
            return 0;
        }
        if (isNaN(value)) {
            return value;
        }
        value = parseFloat(value);
        return value.toFixed(decimals);
    }

    console.log ("[Debug] machine: ", props.machine);

    let content = (
        <>
            <Box
                height="100px"
                display="grid"
                gridTemplateColumns="repeat(12, 1fr)"
                gridAutoRows="90px"
                gap="10px"
            >
                <Box
                    gridColumn="span 2"
                    sx={{
                        paddingTop: "4px",
                        paddingBottom: "8px",
                        paddingX: "12px",
                        minWidth: 0,
                        minHeight: 0,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: //colors.primary[400]
                            machineStatus === "Running"
                                ? colors.dashbordGreen[600]
                                : machineStatus === "Offline" ?
                                    colors.yellow[500]
                                    : machineStatus === "Stopped"
                                        ? colors.redAccent[500]
                                        : colors.primary[400]
                    }}
                >
                    <Box justifyContent="center" display="flex">
                        <Typography variant="h5">
                            <FormattedMessage {...messages.machine} />
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                        <FittedMachineText
                            variant={props.machineDescription?.trim() ? "h2" : "h1"}
                            alignItems={props.machineDescription?.trim() ? "center" : "flex-start"}
                        >
                            {props.machineName}
                        </FittedMachineText>
                        {props.machineDescription?.trim() && (
                            <FittedMachineText variant="h5" flex={0.6}>
                                {props.machineDescription}
                            </FittedMachineText>
                        )}
                    </Box>
                </Box>

                <Box
                    gridColumn="span 1"
                    sx={{
                        paddingTop: "4px",
                        backgroundColor: getBoxColor(t, "t"),
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Box>
                            <Typography variant="h5">
                                <FormattedMessage {...messages.t} />
                            </Typography>
                        </Box>
                    </Box>
                    <Box alignItems="center" justifyContent="center" display="flex">
                        <Box alignItems="baseline" justifyContent="center" display="flex">
                            <Typography variant="h1">
                                {roundOrEmptystring(t, "t")}%
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            paddingRight: 1,
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box />
                        <Box>
                            <FormattedMessage {...messages.targetValue} /> {getAvalabilityThresholds().percentWarning}%
                        </Box>
                    </Box>

                </Box>
                {((isFeatureEnabled(props.featureFlags, 'ShowSpeedUnitType') && props.machine?.speedUnitType?.id === 2) || (isFeatureEnabled(props.featureFlags, 'ShowSpeedUnitType') === false && props.machine?.showPaceAsCycleTime === false)) ?
                    (
                        <Box
                            gridColumn="span 1"
                            sx={{
                                paddingTop: "4px",
                                backgroundColor: paceColor(
                                    takt,
                                    idealTakt
                                ),
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <Box>
                                    <FormattedMessage {...messages.takt} /> {showStringIfNotEmptyValue(takt, "/H", "takt")}
                                </Box>

                            </Box>
                            <Box alignItems="center" justifyContent="center" display="flex">
                                <Box alignItems="baseline" justifyContent="center" display="flex">
                                    <Typography variant="h1">
                                        {roundOrEmptystring(takt, "takt")}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    paddingRight: 1,
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Box />
                                {props.machine?.meassurePerformance && (
                                    <Box>
                                        <FormattedMessage {...messages.targetValue} /> {idealTakt}/H
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ) : 
                    (
                        <Box
                            gridColumn="span 1"
                            sx={{
                                paddingTop: "4px",
                                backgroundColor: getBoxColor(a, "a"),
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <Box>
                                    <Typography variant="h5">
                                        <FormattedMessage {...messages.a} />
                                    </Typography>
                                </Box>
                            </Box>
                            <Box alignItems="center" justifyContent="center" display="flex">
                                <Box alignItems="baseline" justifyContent="center" display="flex">
                                    <Typography variant="h1">
                                        {roundOrEmptystring(a, "a")}%
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    paddingRight: 1,
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Box />
                                <Box>
                                    {props.machine?.meassurePerformance && (
                                        <>
                                            <FormattedMessage {...messages.targetValue} />{" "}
                                            {getPerformanceThresholds().percentWarning}%
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )
                }

                <Box
                    gridColumn="span 1"
                    sx={{
                        paddingTop: "4px",
                        backgroundColor: getBoxColor(k, "k"),
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Box>
                            <Typography variant="h5">
                                <FormattedMessage {...messages.k} />
                            </Typography>
                        </Box>
                    </Box>
                    <Box alignItems="center" justifyContent="center" display="flex">
                        <Box alignItems="baseline" justifyContent="center" display="flex">
                            <Typography variant="h1">
                                {roundOrEmptystring(k, "k")}%
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            paddingRight: 1,
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box />
                        <Box>
                            {props.machine?.meassureQuality && (
                                <>
                                    <FormattedMessage {...messages.targetValue} />{" "}
                                    {getQualityThresholds().percentWarning}%
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Box
                    gridColumn="span 1"
                    sx={{
                        paddingTop: "4px",
                        backgroundColor: getBoxColor(tak, "tak"),
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Box>
                            <FormattedMessage {...messages.tak} />
                        </Box>
                    </Box>
                    <Box alignItems="center" justifyContent="center" display="flex">
                        <Box alignItems="baseline" justifyContent="center" display="flex">
                            <Typography variant="h1">
                                {roundOrEmptystring(tak)}%
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            paddingRight: 1,
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box />
                        <Box>
                            <FormattedMessage {...messages.targetValue} />  {getOeeThresholds().percentWarning}%
                        </Box>
                    </Box>
                </Box>
                <Box
                    gridColumn="span 1"
                    sx={{
                        paddingTop: "4px",
                        backgroundColor: colors.primary[400]
                    }}
                >
                    <Box sx={{
                        display: "flex",
                        justifyContent: "center",
                    }}>
                        {getIdealCycleTimeTitle()}
                    </Box>
                    <Box alignItems="center" justifyContent="center" display="flex">
                        <Typography variant="h1">{ 
                        isFeatureEnabled(props.featureFlags, 'ShowSpeedUnitType') && (props.machine?.speedUnitType?.id === 5) ? getSpeedTypeValueDividedByValueRounded(60, idealCycleTime) : formatToFixed(idealCycleTime, 2)}</Typography>
                    </Box>
                </Box>

                <Box
                    gridColumn="span 1"
                    sx={{
                        paddingTop: "4px",
                        backgroundColor: isFeatureEnabled(props.featureFlags, 'ShowSpeedUnitType') && (props.machine?.speedUnitType?.id === 2 || props.machine?.speedUnitType?.id === 3 || props.machine?.speedUnitType?.id === 4)
                            ? paceColor(lastCycleTime, idealCycleTime) 
                            : isFeatureEnabled(props.featureFlags, 'ShowSpeedUnitType') && (props.machine?.speedUnitType?.id === 5) 
                            ? paceColor(60 / lastCycleTime, 60 / idealCycleTime)
                            : cycleTimeColor(lastCycleTime, idealCycleTime)
                    }}
                >
                    <Box sx={{
                        display: "flex",
                        justifyContent: "center"
                    }}>
                        {getLastCycleTimeTitle()}
                    </Box>
                    <Box alignItems="center" justifyContent="center" display="flex">
                        <Typography variant="h1">{isFeatureEnabled(props.featureFlags, 'ShowSpeedUnitType') && (props.machine?.speedUnitType?.id === 5) ? getSpeedTypeValueDividedByValueRounded(60, lastCycleTime) : lastCycleTime}</Typography>
                    </Box>
                </Box>
                <Box
                    gridColumn="span 4"
                    sx={{
                        backgroundColor: colors.primary[400],
                    }}>
                    <Box sx={{ flexDirection: 'column' }}>
                        <DateFilter
                            onDataReceived={props.handleTimeSpan}
                            startDate={props.datePickerStartDate}
                            endDate={props.datePickerEndDate}
                            setStartDate={props.setStartDate}
                            setEndDate={props.setEndDate} />
                        <Box justifyContent="end" display="flex" pr="35px" pt="15px">
                            <Typography variant="h5">{formatFullDateTime(props.startDate)} --- {formatFullDateTime(props.endDate)}</Typography>
                        </Box>
                    </Box>
                </Box>

            </Box>
            {props.tabExpanded === false && <>
                <Box
                    height="260px"
                    display="grid"
                    gridTemplateColumns="repeat(12, 1fr)"
                    gridAutoRows="75px"
                    gap="10px"
                >
                    <Box
                        gridColumn="span 8"
                        height="260px"
                        display="grid"
                        gridTemplateColumns="repeat(8, 1fr)"
                        gridAutoRows="75px"
                        gap="10px"
                    >


                        <Box
                            gridColumn="span 1"
                            sx={{
                                paddingTop: "4px",
                                backgroundColor: colors.primary[400]
                            }}
                        >
                            <Box justifyContent="center" display="flex">
                                <Typography variant="h6">
                                    <FormattedMessage {...messages.nrOfCycles} />
                                </Typography>
                            </Box>
                            <Box justifyContent="center" display="flex">
                                <Box>
                                    <Typography variant="h3">
                                        {numberOfCyclesWithCavity.toLocaleString("sv-SE")}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            gridColumn="span 1"
                            sx={{
                                paddingTop: "4px",
                                backgroundColor: colors.primary[400]
                            }}
                        >
                            <Box justifyContent="center" display="flex">
                                <Typography variant="h6">
                                    <FormattedMessage {...messages.scrappedQty} />
                                </Typography>
                            </Box>
                            <Box justifyContent="center" display="flex">
                                <Box>
                                    <Typography variant="h3">
                                        {scrappedQty.toLocaleString("sv-SE")}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            gridColumn="span 1"
                            sx={{
                                paddingTop: "4px",
                                backgroundColor: colors.primary[400]
                            }}
                        >
                            <Box justifyContent="center" display="flex">
                                <Typography variant="h6">
                                    <FormattedMessage {...messages.remainingTime} />
                                </Typography>
                            </Box>
                            <Box justifyContent="center" display="flex">
                                <Stack direction="column" spacing={0} justifyContent="center" justifyItems="center" textAlign="center" display="flex">
                                    <Typography variant="h3">
                                        {remainingTimeRaw && remainingTimeRaw > 0
                                            ? formatMinutesAsDuration(remainingTimeRaw * 60)
                                            : "-"
                                        }
                                    </Typography>
                                    <Typography fontSize="10px">
                                        { remainingTimeRaw && remainingTimeRaw > 0
                                            ? dayjs().add(remainingTimeRaw * 60, 'minute').format("YYYY-MM-DD HH:mm")
                                            : ""
                                        }
                                    </Typography>
                                </Stack>
                            </Box>
                        </Box>

                        <Box
                            gridColumn="span 1"
                            sx={{
                                paddingTop: "4px",
                                backgroundColor: colors.primary[400]
                            }}
                        >
                            <Box justifyContent="center" display="flex">
                                <Tooltip title={<Typography sx={{ whiteSpace: "pre-line" }} fontSize="14px">{formatMessage(messages.uptimeExplenation)}</Typography>}>
                                    <Typography variant="h6" alignItems="center" display="flex">
                                        <FormattedMessage {...messages.uptime} /> <IconInformation sx={{ fontSize: 16, ml: 0.5 }} />
                                    </Typography>
                                </Tooltip>
                            </Box>
                            <Box justifyContent="center" display="flex">
                                <Box>
                                    <Typography variant="h3">
                                        {formatMinutesAsDuration(uptime)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            gridColumn="span 2"
                            sx={{
                                paddingTop: "4px",
                                backgroundColor: colors.primary[400]
                            }}
                        >
                            <Box justifyContent="center" display="flex">
                                <Tooltip title={<Typography sx={{ whiteSpace: "pre-line" }} fontSize="14px">{formatMessage(messages.downtimeExplenation)}</Typography>}>
                                    <Typography variant="h6" alignItems="center" display="flex">
                                        <FormattedMessage {...messages.downtime} /> <IconInformation sx={{ fontSize: 16, ml: 0.5 }} />
                                    </Typography>
                                </Tooltip>
                            </Box>
                            <Box justifyContent="center" display="flex">
                                <Box>
                                    <Typography variant="h3">
                                        {formatMinutesAsDuration(downTime)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box
                            gridColumn="span 2"
                            sx={{
                                paddingTop: "4px",
                                backgroundColor: colors.primary[400]
                            }}
                        >
                            <Box justifyContent="center" display="flex">
                                <Tooltip title={<Typography sx={{ whiteSpace: "pre-line" }} fontSize="14px">{formatMessage(messages.downtimeNoOeeExplenation)}</Typography>}>
                                    <Typography variant="h6" alignItems="center" display="flex">
                                        <FormattedMessage {...messages.downtimeNoOee} /> <IconInformation sx={{ fontSize: 16, ml: 0.5 }} />
                                    </Typography>
                                </Tooltip>
                            </Box>
                            <Box justifyContent="center" display="flex">
                                <Box>
                                    <Typography variant="h3">
                                        {formatMinutesAsDuration(downTimeNoOee)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>


                    </Box>




                    <Box
                        gridColumn="span 4"
                        gridRow="span 3"
                        sx={{
                            backgroundColor: colors.primary[400],
                        }}
                    >
                        <Box display="flex" height="100%">
                            {/* Comment section (10/12) */}
                            <Box flex={11} onClick={() => props.handleOpenCommentClick()}>
                                <Box sx={{ padding: 1 }}>
                                    <FormattedMessage {...messages.machineComments} />
                                </Box>
                                {/* add vertical scroll to comment box if comment is long */}
                                <Box component="button" onClick={() => props.handleOpenCommentClick()} style={{ all: 'unset', cursor: 'pointer' }}>
                                    <Box sx={{ padding: 1 }} height="180px" width="95%" overflow="auto" textOverflow="ellipsis">

                                        <Typography variant="h6" >
                                            {props.machineComment?.split(/\n/g)?.map((line, index) => (
                                                <Fragment key={index}>
                                                    {line}
                                                    <br />
                                                </Fragment>
                                            ))}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider orientation="vertical" flexItem sx={{ backgroundColor: 'white', mx: 1, width: '1px' }} />

                            <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" pr={1} pl={0}>
                                {/* CMMS is unreleased — keep it off this OEE page unless the module is on. */}
                                {props.cmmsEnabled &&
                                    <MachineReportIssueButton
                                        compact
                                        machineId={props.machineId}
                                        machineName={props.machineName}
                                        companyKey={props.companyKey}
                                    />
                                }
                                <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.autocode} /></Typography>}>
                                    <IconButton aria-label="Autocode" size="large" onClick={props.handleOpenAutocodeClick}>
                                        <HdrAutoOutlined fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                                <HideIfNoRole anyOf={[1]}>
                                    <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.settingsButtonTooltip} /></Typography>}>
                                        <IconButton aria-label="Settings" size="large" onClick={props.handleOpenSettingsClick}>
                                            <SettingsOutlinedIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </HideIfNoRole>
                                <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.commentButtonTooltip} /></Typography>}>
                                    <IconButton aria-label="Open machine comment" size="large" onClick={props.handleOpenCommentClick}>
                                        <ChatOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                                {props.company?.grafanaBaseUrl && (
                                    <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.grafanaButtonTooltip} /></Typography>}>
                                        <IconButton size="large" onClick={handleGrafanaLinkClick} target="_blank">
                                            <AnalyticsOutlinedIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.plcLogButtonTooltip} /></Typography>}>
                                    <IconButton size="large" onClick={props.handlePlcLogClick}>
                                        <FormatListBulletedOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                    {/* </Box> */}

                    <Box
                        gridColumn="span 8"
                        display="grid"
                        gridTemplateColumns="repeat(9, 1fr)"
                        gridAutoRows="50px"
                        columnGap="0px"
                        rowGap="2px"
                        marginBottom="15px"
                    >
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                                paddingTop: "10px"
                            }}
                            gridColumn={(isFeatureEnabled(props.featureFlags, 'UseOEEAsPOMaster') || isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportQty') || isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportScrap')) ? "span 2" : "span 3"}
                        >
                            <Typography variant="h5">
                                <FormattedMessage {...messages.item} />
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                                paddingTop: "10px"
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                <FormattedMessage {...messages.productionOrder} />
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                                paddingTop: "10px"
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                <FormattedMessage {...messages.plannedQty} />
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                                paddingTop: "10px"
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                <FormattedMessage {...messages.reportedQty} />
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                                paddingTop: "10px"
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                <FormattedMessage {...messages.remainingQty} />
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                                paddingTop: "10px"
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                <FormattedMessage {...messages.percentageDone} />
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                                paddingTop: "10px"
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                {/* <FormattedMessage {...messages.cavity} /> */}
                                <FormattedMessage {...messages.po_table_cavity} />
                            </Typography>
                        </Box>
                        {(isFeatureEnabled(props.featureFlags, 'UseOEEAsPOMaster') || isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportQty') || isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportScrap')) &&
                            <Box
                                sx={{
                                    backgroundColor: colors.primary[400],
                                    padding: "12px"
                                }}
                                gridColumn="span 1"
                                gap="10px"
                            >
                                <Typography variant="h5">

                                </Typography>
                            </Box>
                        }



                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",

                            }}
                            gridColumn={(isFeatureEnabled(props.featureFlags, 'UseOEEAsPOMaster') || isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportQty') || isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportScrap')) ? "span 2" : "span 3"}
                        >
                            <Typography variant="h5">
                                {getItemInProduction(machineInfo.activeItem, machineInfo.activeItemName)}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                {machineInfo.activeProductionOrder}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                {machineInfo.plannedQty.toLocaleString("sv-SE")}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                {machineInfo.reportedQty.toLocaleString("sv-SE")}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px"
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                {machineInfo.remainingQty.toLocaleString("sv-SE")}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px"
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                {percentageDone}%
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                padding: "12px",
                            }}
                            gridColumn="span 1"
                            gap="10px"
                        >
                            <Typography variant="h5">
                                {props.machine?.activeToolCavities?.toLocaleString("sv-SE") || "-"}
                            </Typography>
                        </Box>
                        { isFeatureEnabled(props.featureFlags, 'UseOEEAsPOMaster') &&
                            <Box
                                sx={{
                                    backgroundColor: colors.primary[400],
                                    paddingTop: "3px"
                                }}
                                gridColumn="span 1"
                                gap="10px"
                            //textAlign="flex-start"
                            >
                                <Stack direction="row" spacing={"5px"} justifyContent="flex-start" sx={{ paddingRight: "10px" }}>

                                    {/* <Button
                                        variant="contained"
                                        sx={{
                                            marginTop: 2,
                                            marginLeft: "auto",
                                            display: "block",
                                            width: "120px",
                                        }}
                                        onClick={() => { props.setSelectedTab(2); props.setReportPO(true); }}
                                        disabled={machineInfo.activeProductionOrder === "#NA" ? true : false}
                                    >
                                        <MoveToInboxOutlinedIcon fontSize="small" /> Rapportera
                                    </Button> */}


                                    <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.startLabel} /></Typography>}>
                                        <Box>
                                            <IconButton variant="contained" size="medium" onClick={() => { setActivePOAction("start"); setOpenPopup(true); }}
                                                sx={{
                                                    // ml: 1, "&:hover": {
                                                    //     backgroundColor: colors.primary[500],
                                                    // },
                                                    backgroundColor: colors.primary[400],
                                                }}>
                                                {/* <StartIcon fontSize="medium" /> */}
                                                <PlayCircleFilledWhiteOutlinedIcon fontSize="large" />
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.reportLabel} /></Typography>}>
                                        <Box>
                                            <IconButton variant="contained" size="medium"
                                                disabled={machineInfo.activeProductionOrder === "#NA" ? true : false}
                                                onClick={() => { setActivePOAction("report"); setOpenPopup(true); }} sx={{
                                                    // ml: 1, "&:hover": {
                                                    //     backgroundColor: colors.primary[500],
                                                    // },
                                                    backgroundColor: colors.primary[400],
                                                }}>
                                                {/* <InputOutlinedIcon fontSize="medium" /> */}
                                                <MoveToInboxOutlinedIcon fontSize="large" />
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.stopLabel} /></Typography>}>
                                        <Box>
                                            <IconButton variant="contained" size="medium"
                                                disabled={machineInfo.activeProductionOrder === "#NA" ? true : false}
                                                onClick={() => { setActivePOAction("stop"); setOpenPopup(true); }} sx={{
                                                    // ml: 1, "&:hover": {
                                                    //     backgroundColor: colors.primary[500],
                                                    // },
                                                    backgroundColor: colors.primary[400],
                                                }}>
                                                {/* <KeyboardTabIcon fontSize="medium" /> */}
                                                <StopCircleOutlinedIcon fontSize="large" />
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                </Stack>

                            </Box>
                        }

                        {(isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportQty') || isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportScrap')) &&
                            <Box
                                sx={{
                                    backgroundColor: colors.primary[400],
                                    paddingTop: "5px"
                                }}
                               // gridColumn="span 1"
                                //gap="10px"
                            //textAlign="flex-start"
                            >
                                {/* <Stack direction="row" spacing={1} justifyContent="flex-start" sx={{ paddingRight: "10px" }}> */}
                                    <Tooltip title={<Typography fontSize="12px"><FormattedMessage id="axionoee.features.machineDetails.components.tooltip.reportForMachine" /></Typography>}>
                                        <Box>
                                            <Button
                                                variant="contained"
                                                sx={{
                                                    //marginTop: 2,
                                                    marginLeft: "auto",
                                                    marginRight:"5px",
                                                    display: "block",
                                                    width: "120px",
                                                }}
                                                onClick={() => { props.setSelectedTab(2); props.setReportPO(true); }}
                                                disabled={machineInfo.activeProductionOrder === "#NA" ? true : false}
                                            >
                                                {isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportQty') && (
                                                    <>
                                                        <MoveToInboxOutlinedIcon fontSize="small" /> 
                                                        <FormattedMessage id="axionoee.features.machineDetails.components.label.btn_report" />
                                                    </>
                                                )}
                                                {(isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportScrap') && !isFeatureEnabled(props.featureFlags, 'UseProductionOrderTableAsPOMasterReportQty')) && (
                                                    <>
                                                        {/* <DeleteForeverIcon fontSize="small" /> */}
                                                        <DeleteOutlinedIcon fontSize="small" />
                                                         <FormattedMessage id="axionoee.features.machineDetails.components.label.btn_report" />
                                                    </>
                                                )}
                                            </Button>
                                        </Box>
                                    </Tooltip>
                                {/* </Stack> */}
                            </Box>
                        }
                        <Box
                            //sx={{ backgroundColor: "#ff00ff", height: "100%" }}
                            gridColumn="span 8"
                        >
                        </Box>
                    </Box>
                </Box>
                <Popup
                    openPopup={openPopup}
                    setOpenPopup={setOpenPopup}
                    maxWidht="sm"
                    title={formatMessage(messages.dialogheadding) + props.machineName}//{<FormattedMessage {...messages.importProductionItemLabel} />}
                >
                    <ActivePOUpdateDialog
                        machineId={props.machineId}
                        action={activePOAction}
                        //setShowBackdrop={setShowBackdrop}
                        //handleRefreshData={activeProductionOrdersQuery.refetch}
                        setOpenPopup={setOpenPopup}
                        invalidateMachineInfo={props.invalidateMachineInfo}
                    />
                </Popup>
            </>}
        </>
    );

    return <>{content}</>;
};

export default Infoboxes;
